import React, { useCallback, useMemo, useState } from "react";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";
import { evaluateNumberExpression } from "../../../shared/utils/expression.utils";

interface InsertInlineTagProps {
    tagname: string,
    string?: string,
    startindex?: number,
    endindex?: number,
    children?: React.ReactNode,
}

/***
 * A source component that inserts an inline tag in a child element.
 * The tag is inserted at the startIndex and endIndex.
 * 
 * Params:
 *      tagName: The name of the tag to insert
 *      string: The string to insert the tag in
 *      startIndex: The start index of the tag
 *      endIndex: The end index of the tag
 */
export const InsertInlineTag = React.memo(function InsertInlineTag({ tagname, string, startindex, endindex, children }: InsertInlineTagProps): React.ReactElement {

    const stringChild = useMemo(() => findFirstComponentOfType(children, "arg-string"), [children]);
    const [stringValue, setStringValue] = useState(string);

    // Get the start index from the start index child
    const startIndexChild = useMemo(() => findFirstComponentOfType(children, "arg-startindex"), [children]);
    const [startIndexValue, setStartIndexValue] = useState(startindex);
    const setStartIndexStringValue = useCallback((value: string) => {
        if (value) {
            const numberValue = evaluateNumberExpression(value);
            if (!isNaN(numberValue)) {
                setStartIndexValue(numberValue);
            }
        }
    }, [setStartIndexValue]);

    // Get the end index from the end index child
    const endIndexChild = useMemo(() => findFirstComponentOfType(children, "arg-endindex"), [children]);
    const [endIndexValue, setEndIndexValue] = useState(endindex);
    const setEndIndexStringValue = useCallback((value: string) => {
        if (value) {
            const numberValue = evaluateNumberExpression(value);
            if (!isNaN(numberValue)) {
                setEndIndexValue(numberValue);
            }
        }
    }, [setEndIndexValue]);

    const resultRawHtml = useMemo(() => {
        if (stringValue && tagname && startIndexValue !== undefined && endIndexValue !== undefined
            && stringValue.length > startIndexValue && stringValue.length > endIndexValue
            && startIndexValue <= endIndexValue) {
            return `${stringValue.substring(0, startIndexValue)}<${tagname}>${stringValue.substring(startIndexValue, endIndexValue)}</${tagname}>${stringValue.substring(endIndexValue)}`;
        }
        return stringValue;
    }, [tagname, startIndexValue, endIndexValue, stringValue]);

    return <>
        {resultRawHtml}
        {stringChild && <TextExtractor onExtractedText={setStringValue}>
            {stringChild.props.children}
        </TextExtractor>}
        {startIndexChild && <TextExtractor onExtractedText={setStartIndexStringValue}>
            {startIndexChild.props.children}
        </TextExtractor>}
        {endIndexChild && <TextExtractor onExtractedText={setEndIndexStringValue}>
            {endIndexChild.props.children}
        </TextExtractor>}
    </>;
});